import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Typography,
  Grid,
  Pagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
  Stepper,
  Step,
  StepLabel,
  Button,
  TableCell,
  TableRow,
  TableBody,
  TableHead,
  Table,
  TableContainer,
  Chip,
  Link,
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventIcon from '@mui/icons-material/Event';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import DateRangeIcon from '@mui/icons-material/DateRange';
import SourceIcon from '@mui/icons-material/Source';
import SoapIcon from '@mui/icons-material/Soap';
import Person2Icon from '@mui/icons-material/Person2';
import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal';
import GroupIcon from '@mui/icons-material/Group';
import InfoIcon from '@mui/icons-material/Info';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import GppGoodIcon from '@mui/icons-material/GppGood';
import GppBadIcon from '@mui/icons-material/GppBad';
import DirectionsIcon from '@mui/icons-material/Directions';
import TimeIcon from '@mui/icons-material/AccessTime';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CarCrashIcon from '@mui/icons-material/CarCrash';
import NoCrashIcon from '@mui/icons-material/NoCrash';
import api from '../../api/api';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import ApproveRequestDialogManager from './ApproveRequestDialogManager';
import CheckBox from '@mui/icons-material/CheckBox';
import DownloadIcon from '@mui/icons-material/Download';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ApproveRequestDialogAdmin from './ApproveRequestDialogAdmin';
import { serialize } from 'object-to-formdata';
import * as XLSX from "xlsx";

const PAGE_SIZE = 10;
const ExpenseRequestListingPage = () => {

  var utc = require("dayjs/plugin/utc");
  dayjs.extend(utc);

  const navigate = useNavigate();

  const { user } = useAuth();

  const [expenseRequests, setExpenseRequests] = useState([]);

  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const [userId, setUserId] = useState(null);
  const [role, setRole] = useState(null);

  useEffect(() => {
    if (user) {
      setUserId(user['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']);
      setRole(user['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']);
    }
  }, [user]);

  const [filteredData, paginatedData] = useMemo(() => {
    const filteredData = expenseRequests?.filter(
      req => req.employeeId == userId ||
        (role == "Manager" && req.approvalManagerId == userId) ||
        (role == "Admin" && req.managerApprovedAt)
    );
    return [filteredData, filteredData?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)];
  }, [userId, role, page, expenseRequests]);

  const handleApproveOpenDialog = (request) => {
    setSelectedRequest(request);
    setDialogOpen('approve');
  };

  const handleRejectOpenDialog = (request) => {
    setSelectedRequest(request);
    setDialogOpen('reject');
  };

  const handleCloseDialog = () => {
    setDialogOpen(null);
    setSelectedRequest(null);
  };


  const handleApproveManager = (remark) => {

    if (dialogOpen === 'approve') {
      selectedRequest.managerApprovedAt = dayjs().utc('z').format();
    } else {
      selectedRequest.managerRejectedAt = dayjs().utc('z').format();
    }

    selectedRequest.managerRemarks = remark;

    api.put(`/api/ExpenseRequests/${selectedRequest.id}`, selectedRequest)
      .then(response => {
        loadRequests();
        handleCloseDialog();
      })
      .catch(error => {
        console.error('Error submitting request', error);
        handleCloseDialog();
        // Handle error
      });

  };

  const handleApproveAdmin = (remark, files) => {

    selectedRequest.adminApprovedAt = dayjs().utc('z').format();
    selectedRequest.adminRemarks = remark;

    api.put(`/api/ExpenseRequests/${selectedRequest.id}`, selectedRequest)
      .then(response => {
        loadRequests();
        handleCloseDialog();
      })
      .catch(error => {
        console.error('Error submitting request', error);
        handleCloseDialog();
        // Handle error
      });
  };

  const loadRequests = () => {
    api.get('/api/ExpenseRequests')
      .then(response => {
        setExpenseRequests(response.data);
      })
      .catch(error => {
        console.error('Error fetching managers', error);
      });
  }

  useEffect(() => {
    loadRequests();
  }, []);

  const getFileIcon = (file) => {
    const extension = file.name.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return <PictureAsPdfIcon />;
      case 'jpg':
      case 'jpeg':
      case 'png':
        return <ImageIcon />;
      case 'xls':
      case 'xlsx':
        return <InsertDriveFileIcon />;
      case 'doc':
      case 'docx':
        return <InsertDriveFileIcon />;
      default:
        return <AttachFileIcon />;
    }
  };

  const handleDownload = async (e) => {

    let wb = XLSX.utils.book_new(),
      pws = XLSX.utils.json_to_sheet([]);

    let carRequestHeading = [[
      "Request Id",
      "Requester",
      "Requested At",
      "Approval Manager",
      "Visiting Place",
      "Visit Days",
      "Purpose Of Visit",
      "No Of Person",
      "Remarks",
      "Total Amount",
      "Manager Approved At",
      "Manager Rejected At",
      "Manager Remarks",
      "Admin Approved At",
      "Admin Remarks"
    ]];

    const carRequestData = filteredData.map((c) => ({
      RequestId: c.requestId,
      Requester: c.employee?.name,
      RequestedAt: dayjs(c.createdAt).format("DD/MM/YYYY hh:mm a"),
      ApprovalManager: c.approvalManager?.name,
      VisitingPlace: c.visitingPlace,
      VisitDays: c.visitDays,
      PurposeOfVisit: c.purposeOfVisit,
      NumberOfPerson: c.noOfPerson,
      Remarks: c.remarks,
      TotalAmount: Math.floor(c.expenses.reduce((sum, expense) => sum + new Number(expense.amount), 0)).toFixed(2),
      ManagerApprovedAt: c.managerApprovedAt && dayjs(c.managerApprovedAt).format("DD/MM/YYYY hh:mm a"),
      ManagerRejectedAt: c.managerRejectedAt && dayjs(c.managerRejectedAt).format("DD/MM/YYYY hh:mm a"),
      ManagerRemarks: c.managerRemarks,
      AdminApprovedAt: c.adminApprovedAt && dayjs(c.adminApprovedAt).format("DD/MM/YYYY hh:mm a"),
      AdminRemarks: c.adminRemarks
    }));


    XLSX.utils.sheet_add_aoa(pws, carRequestHeading);
    XLSX.utils.sheet_add_json(pws, carRequestData, { origin: 'A2', skipHeader: true });

    XLSX.utils.book_append_sheet(wb, pws, "Expense Claim Requests");

    XLSX.writeFile(wb, "Export.xlsx");
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box mb={2} display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
        <Typography component="p" variant="h5">Expense Claim</Typography>
        {
          role == "Admin" &&
          <Button size='small' variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}>
            Excel
          </Button>
        }
      </Box>
      <Grid container spacing={2}>
        {paginatedData?.map((request) => (
          <Grid item xs={12} key={request.id}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${request.id}-content`}
                id={`panel${request.id}-header`}
              >
                <Box sx={{
                  width: '100%', display: 'grid'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PersonIcon sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      <b>Requester:</b> {request.employee.name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <SourceIcon sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      <b>Request Id:</b> {request.requestId}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <MyLocationIcon sx={{ mr: 1 }} />
                    <Typography variant="body1"
                      sx={{
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        WebkitLineClamp: 2,
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal'
                      }}
                    >
                      <b>Visiting Place:</b> {request.visitingPlace}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CurrencyRupeeIcon sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      <b>Total Amount:</b> {Math.floor(request.expenses.reduce((sum, expense) => sum + new Number(expense.amount), 0)).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Person2Icon sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      <b>Manager:</b> {request.approvalManager.name}
                    </Typography>
                  </Box>
                  {request.canceledBy &&
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PersonIcon color='warning' sx={{ mr: 1 }} />
                      <Typography variant="body1">
                        <b>Canceled By:</b> {request.canceledBy.name}
                      </Typography>
                    </Box>
                  }
                  <Box sx={{ width: '100%', overflowX: 'auto', display: 'block' }}>
                    <Stepper activeStep={request.adminApprovedAt || request.adminRejectedAt ? 3 : request.managerApprovedAt ? 2 : 1}>
                      <Step key={'request'} >
                        <StepLabel>Request</StepLabel>
                      </Step>
                      <Step key={'manager'}>
                        <StepLabel {...{ error: (request.managerRejectedAt != null) }}>Manager</StepLabel>
                      </Step>
                      <Step key={'admin'}>
                        <StepLabel {...{ error: (request.adminRejectedAt != null) }}>Admin</StepLabel>
                      </Step>
                    </Stepper>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <DateRangeIcon sx={{ mr: 1 }} />
                    <Typography variant="body2"><b>Visit Days:</b> {request.visitDays}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <InfoIcon sx={{ mr: 1 }} />
                    <Typography variant="body2"><b>Purpose of Visit:</b> {request.purposeOfVisit}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <GroupIcon sx={{ mr: 1 }} />
                    <Typography variant="body2"><b>No. of Persons:</b> {request.noOfPerson}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <SpeakerNotesIcon sx={{ mr: 1 }} />
                    <Typography variant="body2"><b>Remarks:</b> {request.remarks}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <InsertInvitationIcon sx={{ mr: 1 }} />
                    <Typography variant="body2"><b>Requested At:</b> {dayjs(request.createdAt).format("DD/MM/YYYY hh:mm a")}</Typography>
                  </Box>
                  {request.managerRemarks &&
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {request.managerApprovedAt ? <GppGoodIcon color='success' sx={{ mr: 1 }} /> : <GppBadIcon color='error' sx={{ mr: 1 }} />}
                      <Typography variant="body2"><b>Manager Remark:</b> {request.managerRemarks}</Typography>
                    </Box>
                  }
                  {request.adminRemarks &&
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {request.adminApprovedAt ? <GppGoodIcon color='success' sx={{ mr: 1 }} /> : <GppBadIcon color='error' sx={{ mr: 1 }} />}
                      <Typography variant="body2"><b>Admin Remark:</b> {request.adminRemarks}</Typography>
                    </Box>
                  }
                  {
                    <TableContainer key={request.id} component={Box} my={1}>
                      <Table size="small" aria-label="a dense table">
                        <TableHead>
                          <TableRow>
                            <TableCell>Expense Type</TableCell>
                            <TableCell align='center'>Bill Available</TableCell>
                            <TableCell align='right'>Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {request.expenses.map((expense) => (
                            <TableRow
                              key={expense.id}
                              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                              <TableCell>{expense.expenseType == 0 ? 'Food' : expense.expenseType == 1 ? 'Local Travel' : expense.expenseType == 2 ? 'Beverages' : expense.expenseType == 3 ? 'Incidental' : 'Other'}</TableCell>
                              <TableCell align='center'> {expense.isBillAvailable ? <CheckBox /> : <CheckBoxOutlineBlankIcon />}</TableCell>
                              <TableCell align='right'>{Math.floor(expense.amount).toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                        <TableHead>
                          <TableRow>
                            <TableCell>Total</TableCell>
                            <TableCell></TableCell>
                            <TableCell align='right'>{Math.floor(request.expenses.reduce((sum, expense) => sum + new Number(expense.amount), 0)).toFixed(2)}</TableCell>
                          </TableRow>
                        </TableHead>
                      </Table>
                    </TableContainer>
                  }
                  <Grid container rowSpacing={1} columnSpacing={1}>
                    {request.expenseDocuments.map((file, index) => (
                      <Grid item key={index}>
                        <Link download={file.document.name} target="_blank" rel="noopener" href={`${process.env.REACT_APP_BASE_URL}/${file.document.path}`}>
                          <Chip
                            icon={getFileIcon(file.document)}
                            label={file.document.name}
                          />
                        </Link>
                      </Grid>
                    ))}
                  </Grid>
                  {
                    request.completeddAt == null &&
                    <>
                      {((role == "Admin" && !request.adminApprovedAt && !request.adminRejectedAt) || (role == "Manager" && !request.managerApprovedAt && !request.managerRejectedAt)) &&
                        <Box sx={{ width: '100%', display: 'flex', gap: 2, mb: 1, }}>
                          <Button onClick={() => handleApproveOpenDialog(request)} variant='contained' color='success' sx={{ flex: '1 1 auto' }} >Approve</Button>
                          {role != "Admin" && <Button onClick={() => handleRejectOpenDialog(request)} variant='contained' color='error' sx={{ flex: '1 1 auto' }} >Reject</Button>}
                        </Box>
                      }
                      {/* <Box sx={{ width: '100%', display: 'flex', gap: 2, mb: 1, }}>
                        {role != "Admin" && !request.managerRejectedAt && !request.canceledBy && <Button fullWidth onClick={() => handleCancelRequest(request)} variant='contained' color='warning' sx={{ flex: '1 1 auto' }} >Cancel Request</Button>}
                        {role == "Requester" && !request.managerRejectedAt && !request.canceledBy && checkTripTime(request.journeyDate, request.arrivalTime) && <Button fullWidth onClick={() => handleCompleteRequest(request)} variant='contained' color='success' sx={{ flex: '1 1 auto' }} >Complete Trip</Button>}
                      </Box> */}
                    </>
                  }
                </Box>
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 6 }}>
        <Pagination
          count={Math.ceil(filteredData.length / PAGE_SIZE)}
          page={page}
          siblingCount={1}
          boundaryCount={1}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>
      <Fab color="primary" aria-label="add" sx={{ position: 'fixed', bottom: '2rem', right: '2rem' }} onClick={() => navigate('/expense-request/add')}>
        <AddIcon />
      </Fab>
      {role == "Manager" &&
        <ApproveRequestDialogManager open={dialogOpen != null} isApprove={dialogOpen === 'approve'} handleClose={handleCloseDialog} handleApprove={handleApproveManager} />
      }
      {role == "Admin" &&
        <ApproveRequestDialogAdmin open={dialogOpen != null} handleClose={handleCloseDialog} handleApprove={handleApproveAdmin} />
      }
    </Box>
  );
};

export default ExpenseRequestListingPage;
